import { useEffect, useRef, useState } from 'react'
import { CalendarDays, Search, Wrench, X } from 'lucide-react'
import * as planningApi from '../../../lib/api/v2/planning'
import type { PlanningProjectSearchRowDto, ScheduleEventDto } from '../../../lib/api/v2/planning'
import { cn } from '../../../lib/utils'
import { eventDateLabel, kindColor, searchKindLabel } from './planning-helpers'

interface ProjectSearchBoxProps {
  /** Selecting a project opens its detail drawer and jumps the agenda to its last planned element. */
  onSelectProject: (row: PlanningProjectSearchRowDto) => void
  /** Selecting an appointment / internal work opens its fiche and jumps the agenda to its week. */
  onSelectEvent: (ev: ScheduleEventDto) => void
}

/** DD/MM for a result's last planned element (Brussels), or null. */
function plannedLabel(iso: string | null): string {
  if (!iso) return 'à planifier'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'à planifier'
  return `planifié le ${d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Brussels' })}`
}

/**
 * Search box of the planning toolbar (Elodie 2026-06-14): type a client name or reference, pick a
 * result → the project opens (docked drawer) and the agenda jumps to its last planned element.
 *
 * SOLY-PLAN-008 (Kitchy 2026-09-27): the same query also runs, in parallel, over the TITLE of
 * appointments and internal work, listed in their own section under the projects. The project
 * results render exactly as before. The two requests settle independently — one side failing
 * leaves the other on screen.
 *
 * Debounced; Esc / click-outside close; Enter picks the first hit. Lives in the planning toolbar so
 * it stays reachable even when the À planifier/À suivre aside is docked away.
 */
export function ProjectSearchBox({ onSelectProject, onSelectEvent }: ProjectSearchBoxProps) {
  const [q, setQ] = useState('')
  // Each list remembers the query that produced it. The two requests land independently, so a
  // list is only shown — and only picked by Enter — while it answers the CURRENT query: otherwise a
  // fast answer to the new query would sit beside the previous query's other list, and Enter could
  // open a record that no longer matches (Codex review).
  const [projectHits, setProjectHits] = useState<{ term: string; rows: PlanningProjectSearchRowDto[] }>({ term: '', rows: [] })
  const [eventHits, setEventHits] = useState<{ term: string; rows: ScheduleEventDto[] }>({ term: '', rows: [] })
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = q.trim()
  const rows = projectHits.term === current ? projectHits.rows : []
  const events = eventHits.term === current ? eventHits.rows : []
  const hasResults = rows.length > 0 || events.length > 0

  // Debounced search: only query from 2 chars (1-char hits are too broad to be useful). All state
  // updates live inside the timeout (async) — never synchronously in the effect body.
  useEffect(() => {
    const term = q.trim()
    let active = true
    const t = setTimeout(() => {
      if (!active) return
      if (term.length < 2) { setProjectHits({ term, rows: [] }); setEventHits({ term, rows: [] }); setLoading(false); return }
      setLoading(true)
      // Each side lands on its own: the project results show the moment they arrive, exactly as
      // before the event search existed, and a slow or failing event search never holds them back.
      // The menu opens on whichever answers FIRST, once: the second answer updates the results
      // without reopening a menu the user closed in between (Codex review). « loading » ends with
      // the second.
      let pending = 2
      let opened = false
      const openOnce = () => { if (!opened) { opened = true; setOpen(true) } }
      const settle = () => { pending -= 1; if (pending === 0) setLoading(false) }
      void planningApi.searchPlanningProjects(term)
        .then((res) => { if (active) { setProjectHits({ term, rows: res }); openOnce() } })
        .catch(() => { if (active) setProjectHits({ term, rows: [] }) })
        .finally(() => { if (active) settle() })
      void planningApi.searchPlanningEvents(term)
        .then((res) => { if (active) { setEventHits({ term, rows: res }); openOnce() } })
        .catch(() => { if (active) setEventHits({ term, rows: [] }) })
        .finally(() => { if (active) settle() })
    }, 200)
    return () => { active = false; clearTimeout(t) }
  }, [q])

  // Esc + click-outside close the dropdown (the input keeps its text).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [])

  const reset = () => {
    setOpen(false)
    setQ('')
    setProjectHits({ term: '', rows: [] })
    setEventHits({ term: '', rows: [] })
  }
  const pick = (row: PlanningProjectSearchRowDto) => {
    onSelectProject(row)
    reset()
  }
  const pickEvent = (ev: ScheduleEventDto) => {
    onSelectEvent(ev)
    reset()
  }

  return (
    <div ref={ref} className="relative" data-testid="planning-search">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (hasResults) setOpen(true) }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          if (rows.length > 0) pick(rows[0])
          else if (events.length > 0) pickEvent(events[0])
        }}
        placeholder="Rechercher un chantier ou un rendez-vous…"
        data-testid="planning-search-input"
        // The right padding clears the ✕ only while there is text to clear: empty, it is room the
        // placeholder needs (« …ou un rendez-vous… » was cut off at the toolbar's width).
        className={cn('w-72 rounded-lg border py-1.5 pl-7 text-xs focus:outline-none focus:ring-2 focus:ring-offset-1',
          q.length > 0 ? 'pr-7' : 'pr-2')}
      />
      {q.length > 0 && (
        <button
          type="button"
          onClick={() => { setQ(''); setProjectHits({ term: '', rows: [] }); setEventHits({ term: '', rows: [] }); setOpen(false) }}
          aria-label="Effacer la recherche"
          data-testid="planning-search-clear"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {open && (
        <div
          className="absolute left-0 top-full z-40 mt-1 max-h-80 w-80 overflow-auto rounded-lg border bg-background shadow-lg"
          data-testid="planning-search-results"
        >
          {loading && !hasResults ? (
            <div className="px-3 py-2 text-[11px] text-muted-foreground">Recherche…</div>
          ) : !hasResults ? (
            <div className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="planning-search-empty">Aucun résultat</div>
          ) : (
            <>
              {rows.map((r) => (
                <button
                  key={r.projectId}
                  type="button"
                  onClick={() => pick(r)}
                  data-testid={`planning-search-result-${r.projectId}`}
                  className={cn('flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted')}
                >
                  <span className="truncate text-xs font-medium">{r.clientName}</span>
                  <span className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                    {r.reference && <span className="truncate">{r.reference}</span>}
                    {r.reference && <span aria-hidden>·</span>}
                    <span className="flex-none">{plannedLabel(r.lastPlannedEventStart)}</span>
                  </span>
                </button>
              ))}
              {events.length > 0 && (
                <div data-testid="planning-search-events">
                  <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Rendez-vous
                  </div>
                  {events.map((ev) => {
                    const Icon = ev.kind === 'InternalWork' ? Wrench : CalendarDays
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => pickEvent(ev)}
                        data-testid={`planning-search-event-${ev.id}`}
                        className="flex w-full items-start gap-2 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted"
                      >
                        <Icon className="mt-0.5 h-3.5 w-3.5 flex-none" style={{ color: kindColor(ev.kind) }} aria-hidden />
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <span className="flex min-w-0 items-baseline gap-1.5 text-xs">
                            <span className="flex-none text-[10.5px] text-muted-foreground">{searchKindLabel(ev.kind)}</span>
                            <span className="truncate font-medium">{ev.label}</span>
                          </span>
                          <span className="text-[10.5px] text-muted-foreground">{eventDateLabel(ev)}</span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
